import React, { memo, useMemo } from 'react';
import { Grid, View } from '@adobe/react-spectrum';
import { useSelector } from 'react-redux';
import { useMonthlyTimelineSlice } from './store/monthlyTimelineSlice';
import { useMonthlyMonths } from './hooks/useMonthlyMonths';
import { useExpandedMonths } from './hooks/useExpandedMonths';
import { useMonthlyTimelineLayout } from './hooks/useMonthlyTimelineLayout';
import { useMonthlyTimelineGap } from './hooks/useMonthlyTimelineGap';
import { MonthlyTimelineHeader } from './components/MonthlyTimelineHeader';
import { MonthlySwimlaneGrid } from './components/MonthlySwimlaneGrid';
import { MonthlyMustWinsView } from './MonthlyMustWinsView.Loadable';
import { flattenReleaseProjects } from './utils/flattenReleaseProjects';
import { computeTimelineBodyWidthPx } from './utils/computeTimelineBodyWidthPx';
import {
  sumMonthSectionWidths,
  useMonthSectionWidths
} from './utils/useMonthSectionWidths';
import { getMonthlyLaneOrder } from 'app/shared/filteredReleases/quarterUtils';
import type { MonthlyTimelineLane } from './utils/types';
import {
  selectParkingLot,
  selectCompact,
  selectReleases
} from 'app/pages/Home/slice/selectors';
import { useMonthlySearchFilteredReleases } from './hooks/useMonthlySearchFilteredReleases';
import { useMonthlyKeyDatesLane } from './hooks/useMonthlyKeyDatesLane';
import { useMonthlyNoDataToast } from './hooks/useMonthlyNoDataToast';
import { useMonthlyMustWinViewTransition } from './hooks/useMonthlyMustWinViewTransition';
import { selectLayout } from 'app/pages/Layout/slice/selectors';
import LeftSidebar from 'app/components/RoadmapCard/LeftSidebar';
import { AlertsContainer } from 'app/components/AlertsAndNotification/components/AlertsContainer';
import { ParkingLot } from 'app/components/RoadmapCard/ParkingLot';
import { Legend } from 'app/components/RoadmapCard/Legend';
import { PARKING_LOT_GRID_COLUMN_PX } from 'app/components/RoadmapCard/parkingLotLayoutConstants';
import styles from './MonthlyRoadmap.module.scss';

export const MonthlyRoadmap = memo(() => {
  useMonthlyTimelineSlice();
  useMonthlyNoDataToast();
  const months = useMonthlyMonths();
  const { expandedMonths, toggleMonthExpanded } = useExpandedMonths();
  const releases = useMonthlySearchFilteredReleases();
  const keyDatesLane = useMonthlyKeyDatesLane();
  const showParkingLot = useSelector(selectParkingLot);
  const { displayedMustWinView } = useMonthlyMustWinViewTransition();
  const isCompact = useSelector(selectCompact);
  const allReleasesData = useSelector(selectReleases);
  const { isSidebarOpen } = useSelector(selectLayout);

  const { dayColumns, getDayLeftPx, totalColumns } = useMonthlyTimelineLayout(
    months,
    expandedMonths,
    isCompact
  );

  const monthSections = useMonthSectionWidths(
    months,
    expandedMonths,
    isCompact
  );
  const columnsWidthPx = sumMonthSectionWidths(monthSections);

  const lanes = useMemo<MonthlyTimelineLane[]>(() => {
    const mapped = (releases || [])
      .filter((release: any) => release.type !== 'Key Dates')
      .map((release: any) => ({
        key: release.type.toLowerCase().replace(/\s+/g, '-'),
        label: release.type,
        releaseType: release.type,
        isKeyDate: false,
        items: flattenReleaseProjects(release)
      }));

    const allLanes = keyDatesLane ? [keyDatesLane, ...mapped] : mapped;

    return allLanes.sort(
      (a, b) =>
        getMonthlyLaneOrder(a.releaseType) - getMonthlyLaneOrder(b.releaseType)
    );
  }, [releases, keyDatesLane]);

  const bodyWidthPx = useMemo(
    () => computeTimelineBodyWidthPx(lanes, months, getDayLeftPx, totalColumns),
    [lanes, months, getDayLeftPx, totalColumns]
  );

  const bodyExtensionPx = Math.max(0, bodyWidthPx - columnsWidthPx);

  const gapWidth = useMonthlyTimelineGap({
    expandedMonths,
    isCompact,
    laneCount: lanes.length,
    bodyWidthPx,
    columnsWidthPx
  });

  return (
    <div className={styles.monthlyRoadmapLayout}>
      <div className={styles.monthlyRoadmapAlertsSlot}>
        <AlertsContainer />
      </div>
      <Grid
        data-testid="monthly-roadmap-main-grid"
        UNSAFE_className={styles.monthlyRoadmapGrid}
        areas={
          showParkingLot ? ['sidebar content parking'] : ['sidebar content']
        }
        columns={[
          !isSidebarOpen ? '40px' : '160px',
          '1fr',
          showParkingLot ? `${PARKING_LOT_GRID_COLUMN_PX}px` : '0px'
        ]}
        rows={['minmax(0, 1fr)']}
      >
        <LeftSidebar />
        <View
          gridArea="content"
          UNSAFE_className={styles.monthlyRoadmapContent}
        >
          <View
            data-testid="monthly-roadmap-scroll-area"
            UNSAFE_className={styles.monthlyRoadmapScrollArea}
          >
            <MonthlyTimelineHeader
              monthSections={monthSections}
              bodyExtensionPx={bodyExtensionPx}
              gapWidth={gapWidth}
              onToggleMonthExpanded={toggleMonthExpanded}
              isMustWinView={displayedMustWinView}
            />
            {displayedMustWinView ? (
              <MonthlyMustWinsView
                releases={releases}
                months={months}
                expandedMonths={expandedMonths}
                dayColumns={dayColumns}
                getDayLeftPx={getDayLeftPx}
                totalColumns={totalColumns}
                gapWidth={gapWidth}
              />
            ) : (
              <MonthlySwimlaneGrid
                months={months}
                lanes={lanes}
                expandedMonths={expandedMonths}
                dayColumns={dayColumns}
                getDayLeftPx={getDayLeftPx}
                totalColumns={totalColumns}
                gapWidth={gapWidth}
              />
            )}
          </View>
        </View>
        {showParkingLot && (
          <View gridArea="parking" UNSAFE_className={styles.monthlyParkingLot}>
            <ParkingLot
              showParkingLot={showParkingLot}
              data={allReleasesData?.tbd}
            />
          </View>
        )}
        <Legend />
      </Grid>
    </div>
  );
});
